import { useState, useMemo } from 'react';
import { Card, CardContent } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Checkbox } from '@client/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import { UserMinus, Trash2, Mail, X, Send } from 'lucide-react';

type EnrichedSubscriptionSeat = SubscriptionSeat & {
  advisorEmail: string | null;
  advisorName: string | null;
};

interface SeatsTableProps {
  seats: EnrichedSubscriptionSeat[];
  subscription: any;
  isOwner: boolean;
  isMutating: boolean;
  currentMutation: string | null;
  onRemoveSeat: (seatId: string) => void;
  onResendInvitation: (seatId: string, email: string) => void;
}

export function SeatsTable({
  seats,
  subscription,
  isOwner,
  isMutating,
  currentMutation,
  onRemoveSeat,
  onResendInvitation,
}: SeatsTableProps) {
  const [showAllStatuses, setShowAllStatuses] = useState(false);

  const calculateMonths = (assignedDateTime: Date | null): number | string => {
    if (!assignedDateTime) return '—';
    const assignedDate = new Date(assignedDateTime);
    const today = new Date();
    const yearsDiff = today.getFullYear() - assignedDate.getFullYear();
    const monthsDiff = today.getMonth() - assignedDate.getMonth();
    const totalMonths = yearsDiff * 12 + monthsDiff;
    return Math.floor(totalMonths);
  };

  const sortedSeats = useMemo(() => {
    return seats
      .filter(seat => {
        if (showAllStatuses) return true;
        return (
          seat.status === 'invited' ||
          seat.status === 'active' ||
          seat.status === 'payment_failed' ||
          seat.status === 'paused'
        );
      })
      .sort((a, b) => {
        // First sort by status: active comes first
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;

        // Then sort by assignedDateTime: most recent first
        const dateA = a.assignedDateTime ? new Date(a.assignedDateTime).getTime() : 0;
        const dateB = b.assignedDateTime ? new Date(b.assignedDateTime).getTime() : 0;
        return dateB - dateA;
      });
  }, [seats, showAllStatuses]);

  return (
    <Card className="border-primary-200 mt-6 max-w-[900px] border">
      <CardContent className="p-6">
        <h3 className="text-primary-dark text-lg font-semibold">Seats</h3>
        <p className="mb-4 text-sm text-gray-500">Manage who has access to your subscription.</p>
        <div className="mb-4 flex items-center justify-end gap-2">
          <Checkbox
            id="show-all-statuses"
            checked={showAllStatuses}
            onCheckedChange={checked => setShowAllStatuses(checked === true)}
          />
          <label
            htmlFor="show-all-statuses"
            className="cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show all statuses
          </label>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white">
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Assigned Date</TableHead>
                <TableHead>Months Active</TableHead>
                {isOwner && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSeats.map(seat => (
                <TableRow key={seat.subscriptionSeatId}>
                  <TableCell>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                        seat.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : seat.status === 'invited'
                            ? 'bg-gray-100 text-gray-600'
                            : seat.status === 'invite_declined' || seat.status === 'payment_failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {seat.status.includes('_') ? seat.status.split('_').join(' ') : seat.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{seat.advisorName || '—'}</span>
                      <span className="text-sm text-gray-500">
                        {seat.advisorEmail || seat.invitedEmail || '—'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {seat.assignedDateTime
                      ? new Date(seat.assignedDateTime).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>{calculateMonths(seat.assignedDateTime)}</TableCell>
                  {isOwner && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {seat.status === 'inactive' && !seat.advisorId && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onRemoveSeat(seat.subscriptionSeatId)}
                            title="Remove seat"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {seat.status === 'invited' && (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                onResendInvitation(seat.subscriptionSeatId, seat.invitedEmail || '')
                              }
                              title="Resend invitation"
                              disabled={isMutating && currentMutation === 'invite'}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => onRemoveSeat(seat.subscriptionSeatId)}
                              title="Cancel invitation"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {seat.status === 'payment_failed' && (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                onResendInvitation(seat.subscriptionSeatId, seat.invitedEmail || '')
                              }
                              title="Retry invitation"
                              disabled={isMutating && currentMutation === 'invite'}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => onRemoveSeat(seat.subscriptionSeatId)}
                              title="Remove failed seat"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {seat.status === 'active' &&
                          seat.advisorId !== subscription?.ownerAdvisorId && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => onRemoveSeat(seat.subscriptionSeatId)}
                              title="Remove seat"
                            >
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
